import { type CareCompanionClient, createCareCompanionClient } from '@care-companion/client';
import { CareCompanionService, type CareCompanionSessionApiModel } from '@visiba/api-vcs-client-v2';

const sessions = new Map<string, CareCompanionSessionApiModel>();
const clients = new Map<string, CareCompanionClient>();

export class CareCompanionSessionRequestError extends Error {
	constructor(public readonly status: number) {
		super(`Care Companion session request failed with status ${status}.`);
		this.name = 'CareCompanionSessionRequestError';
	}
}

async function getSession(sessionId?: string): Promise<CareCompanionSessionApiModel> {
	if (sessionId != null) {
		const cachedSession = sessions.get(sessionId);
		if (cachedSession != null && !hasExpired(cachedSession.ExpiresAt)) {
			return cachedSession;
		}
	}

	const response = await CareCompanionService.careCompanionCreateSession({
		body: { SessionId: sessionId ?? null },
	});
	if (response.error !== undefined) {
		throw new CareCompanionSessionRequestError(response.response.status);
	}

	const session = response.data;
	sessions.set(session.SessionId, session);

	return session;
}

export function isSessionNotFound(error: unknown): boolean {
	return error instanceof CareCompanionSessionRequestError && error.status === 404;
}

export interface CareCompanionConnection {
	client: CareCompanionClient;
	sessionId: string;
	conversationId: string;
}

export async function connectCareCompanion(sessionId?: string): Promise<CareCompanionConnection> {
	const currentSession = await getSession(sessionId);
	let client = clients.get(currentSession.SessionId);

	if (client == null) {
		const connectedSessionId = currentSession.SessionId;
		client = createCareCompanionClient({
			baseUrl: currentSession.ApiBaseUrl,
			credentials: 'omit',
			accessToken: async () => (await getSession(connectedSessionId)).AccessToken,
		});
		clients.set(connectedSessionId, client);
	}

	return { client, sessionId: currentSession.SessionId, conversationId: currentSession.ConversationId };
}

export async function endCareCompanionSession(sessionId: string): Promise<void> {
	const response = await CareCompanionService.careCompanionEndSession({
		path: { sessionId },
	});
	if (response.error !== undefined) {
		throw new CareCompanionSessionRequestError(response.response.status);
	}

	sessions.delete(sessionId);
	clients.delete(sessionId);
}

export function getStoredSessionId(licenceHolderId: number): string | null {
	return sessionStorage.getItem(getStorageKey(licenceHolderId));
}

export function storeSessionId(licenceHolderId: number, sessionId: string): void {
	sessionStorage.setItem(getStorageKey(licenceHolderId), sessionId);
}

export function clearStoredSessionId(licenceHolderId: number): void {
	sessionStorage.removeItem(getStorageKey(licenceHolderId));
}

function getStorageKey(licenceHolderId: number): string {
	return `visiba:care-companion:${licenceHolderId}:session`;
}

function hasExpired(expiresAt: string | null): boolean {
	if (expiresAt == null) return false;

	return Date.parse(expiresAt) <= Date.now() + 30_000;
}
